import { useMutation, type UseMutationOptions, useQuery } from "@tanstack/react-query";
import { type AverageSessionsPayload, CredentialsController } from "../controllers/credentialsController";
import { useAppConfig } from "providers";
import { type ApiError } from "api/error/ApiError";
import { onError } from "api/error/errorHandler";
import { type CredentialResponse, type InsuranceResponse } from "components/ProviderProfile/interfaces";
import { type LiabilityInsurancePayload, type LicensePayload } from "../types";

// Initialize controller with API host
const useController = () => {
    const { REACT_APP_CREDENTIALS_HOST } = useAppConfig();
    return new CredentialsController(REACT_APP_CREDENTIALS_HOST);
};

// Credential Mutation Hooks
export const useAddCredential = (options: UseMutationOptions<CredentialResponse, ApiError, LicensePayload>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (payload: LicensePayload) => controller.addCredential(payload, version),
        mutationKey: ['credentials', 'add'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};

export const useUpdateCredential = (options: UseMutationOptions<CredentialResponse, ApiError, LicensePayload>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (payload: LicensePayload) => 
            controller.updateCredential(payload, version),
        mutationKey: ['credentials', 'update'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};

export const useDeleteCredential = (options: UseMutationOptions<void, ApiError, string>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (id: string) => controller.deleteCredential(id, version),
        mutationKey: ['credentials', 'delete'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};

export const useReplaceCredential = (options: UseMutationOptions<CredentialResponse, ApiError, LicensePayload>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (payload: LicensePayload) => 
            controller.replaceCredential(payload, version),
        mutationKey: ['credentials', 'replace'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};

// Insurance Mutation Hooks
export const useAddInsurance = (options: UseMutationOptions<InsuranceResponse, ApiError, LiabilityInsurancePayload>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (payload: LiabilityInsurancePayload) => controller.addInsurance(payload, version),
        mutationKey: ['insurance', 'add'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};

export const useUpdateInsurance = (options: UseMutationOptions<InsuranceResponse, ApiError, LiabilityInsurancePayload>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (payload: LiabilityInsurancePayload) => 
            controller.updateInsurance(payload, version),
        mutationKey: ['insurance', 'update'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};

export const useDeleteInsurance = (options?: UseMutationOptions<void, ApiError, string>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (providerId: string) => controller.deleteInsurance(providerId, version),
        mutationKey: ['insurance', 'delete'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};

export const useReplaceInsurance = (options: UseMutationOptions<InsuranceResponse, ApiError, LiabilityInsurancePayload>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (payload: LiabilityInsurancePayload) => 
            controller.replaceInsurance(payload, version),
        mutationKey: ['insurance', 'replace'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};

export const useFetchAverageCompletedSessions = (params: AverageSessionsPayload, version = 'v1') => {
    const controller = useController();
    return useQuery({
        queryFn: () => controller.fetchAverageCompletedSessions(params, version),
        queryKey: ['average-completed-sessions', params],
    });
};
